import { useEffect, useState } from "react";
import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, Search, Leaf, ChevronDown, Facebook, Instagram, Youtube, Mail, MapPin, Phone, Lock } from "lucide-react";
import { api, BRAND, mediaUrl } from "@/lib/appApi";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/", label: "BERANDA" },
  { to: "/berita", label: "BERITA DLH" },
  { to: "/galeri", label: "GALERI" },
  { to: "/tentang", label: "TENTANG DLH" },
];

function BrandMark({ settings }) {
  const fullLogo = settings?.logo_portal_url ? mediaUrl(settings.logo_portal_url) : null;
  const pemda = settings?.logo_pemda_url ? mediaUrl(settings.logo_pemda_url) : null;
  return (
    <Link to="/" className="flex items-center gap-2.5 group min-w-0" data-testid="brand-home-link">
      {pemda && <img src={pemda} alt="Lambang Kabupaten Mimika" className="h-10 sm:h-12 w-auto object-contain shrink-0" />}
      {fullLogo ? (
        <img src={fullLogo} alt="PAPA BERSERI NEWS — Portal Informasi Lingkungan Hidup Kabupaten Mimika" className="h-9 sm:h-11 w-auto object-contain" />
      ) : (
        <>
          <img src={BRAND.logo} alt="Logo PAPA BERSERI" className="h-11 w-11 rounded-full object-cover ring-2 ring-dlh-greenLight/40" />
          <div className="leading-tight">
            <div className="font-serif font-extrabold text-dlh-green text-lg sm:text-xl tracking-tight">PAPA BERSERI NEWS</div>
            <div className="text-[11px] sm:text-xs text-dlh-greenMid font-medium">Portal Informasi Lingkungan Hidup Kabupaten Mimika</div>
          </div>
        </>
      )}
    </Link>
  );
}

function Header({ settings, units }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const doSearch = (e) => {
    e.preventDefault();
    if (q.trim()) { navigate(`/cari?q=${encodeURIComponent(q.trim())}`); setSearchOpen(false); setMobileOpen(false); }
  };

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-xl border-b border-gray-200 shadow-sm">
      <div className="bg-dlh-green text-white text-[11px] sm:text-xs">
        <div className="max-w-7xl mx-auto px-4 py-1.5 flex justify-between items-center">
          <span className="hidden sm:inline">Mimika Rumah Kita: Bersih, Sehat, Rindang dan Indah</span>
          <span className="sm:hidden">Mimika Rumah Kita</span>
          <span className="text-dlh-greenLight font-medium">PAPA BERSERI = Pilah • Angkut • Pengolahan • Proses Akhir</span>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
        <BrandMark settings={settings} />

        <nav className="hidden lg:flex items-center gap-1" aria-label="Navigasi utama">
          <NavLink to="/" label="BERANDA" />
          <NavLink to="/berita" label="BERITA DLH" />
          <BidangDropdown units={units} />
          <NavLink to="/galeri" label="GALERI" />
          <NavLink to="/tentang" label="TENTANG DLH" />
        </nav>

        <div className="flex items-center gap-2">
          <button onClick={() => setSearchOpen((v) => !v)} data-testid="search-toggle-btn"
            className="p-2.5 rounded-full text-dlh-green hover:bg-dlh-greenPale focus:outline-none focus:ring-2 focus:ring-dlh-orange transition-colors" aria-label="Cari">
            <Search className="h-5 w-5" />
          </button>
          <div className="lg:hidden">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button className="p-2.5 rounded-full text-dlh-green hover:bg-dlh-greenPale focus:ring-2 focus:ring-dlh-orange" data-testid="mobile-menu-btn" aria-label="Menu">
                  <Menu className="h-6 w-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] overflow-y-auto">
                <SheetTitle className="font-serif text-dlh-green">Menu</SheetTitle>
                <nav className="mt-6 flex flex-col gap-1">
                  {NAV.slice(0, 2).map((n) => (
                    <Link key={n.to} to={n.to} onClick={() => setMobileOpen(false)} className="py-3 px-3 rounded-lg text-dlh-green font-semibold hover:bg-dlh-greenPale">{n.label}</Link>
                  ))}
                  <div className="py-2 px-3 text-xs font-bold text-dlh-greenMid uppercase">Bidang DLH</div>
                  {units.map((u) => (
                    <Link key={u.slug} to={`/bidang/${u.slug}`} onClick={() => setMobileOpen(false)} className="py-2.5 px-4 rounded-lg text-sm text-gray-700 hover:bg-dlh-greenPale leading-snug">{u.name}</Link>
                  ))}
                  {NAV.slice(2).map((n) => (
                    <Link key={n.to} to={n.to} onClick={() => setMobileOpen(false)} className="py-3 px-3 rounded-lg text-dlh-green font-semibold hover:bg-dlh-greenPale">{n.label}</Link>
                  ))}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {searchOpen && (
        <div className="border-t border-gray-200 bg-dlh-greenPale">
          <form onSubmit={doSearch} className="max-w-7xl mx-auto px-4 py-3 flex gap-2">
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid="header-search-input"
              placeholder="Cari berita, edukasi, pengumuman…" className="flex-1 rounded-full border border-dlh-green/30 px-5 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-dlh-orange" />
            <Button type="submit" data-testid="header-search-submit" className="bg-dlh-green hover:bg-dlh-greenDark rounded-full px-6">Cari</Button>
          </form>
        </div>
      )}
    </header>
  );
}

function NavLink({ to, label }) {
  const { pathname } = useLocation();
  const active = to === "/" ? pathname === "/" : pathname.startsWith(to);
  return (
    <Link to={to} data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
      className={`px-3 py-2 text-sm font-bold tracking-wide rounded-md transition-colors ${active ? "text-dlh-orange" : "text-dlh-green hover:text-dlh-orange"}`}>
      {label}
    </Link>
  );
}

function BidangDropdown({ units }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="nav-bidang-dropdown" className="px-3 py-2 text-sm font-bold tracking-wide rounded-md text-dlh-green hover:text-dlh-orange flex items-center gap-1 focus:outline-none focus:ring-2 focus:ring-dlh-orange">
        BIDANG DLH <ChevronDown className="h-4 w-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-80 max-w-[90vw]">
        {units.map((u) => (
          <DropdownMenuItem key={u.slug} asChild>
            <Link to={`/bidang/${u.slug}`} data-testid={`bidang-item-${u.slug}`} className="cursor-pointer whitespace-normal leading-snug py-2.5">
              <span className="text-sm text-gray-800">{u.name}</span>
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Footer({ settings, units }) {
  const s = settings || {};
  const sm = s.social_media || {};
  return (
    <footer className="bg-dlh-greenDark text-white mt-16">
      <div className="max-w-7xl mx-auto px-4 py-12 grid gap-10 md:grid-cols-4">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3">
            <Leaf className="h-8 w-8 text-dlh-greenLight" />
            <div>
              <div className="font-serif font-extrabold text-xl">PAPA BERSERI NEWS</div>
              <div className="text-sm text-dlh-greenLight">Portal Informasi Lingkungan Hidup Kabupaten Mimika</div>
            </div>
          </div>
          <p className="mt-4 text-sm text-white/70 max-w-md leading-relaxed">Berita, Edukasi, dan Aksi Nyata untuk Lingkungan. Media publikasi Dinas Lingkungan Hidup Kabupaten Mimika.</p>
          {(s.address || s.email || s.phone) && (
            <div className="mt-4 space-y-1 text-sm text-white/80">
              {s.address && <p className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 shrink-0" />{s.address}</p>}
              {s.email && <p className="flex items-center gap-2"><Mail className="h-4 w-4" />{s.email}</p>}
              {s.phone && <p className="flex items-center gap-2"><Phone className="h-4 w-4" />{s.phone}</p>}
            </div>
          )}
          <div className="mt-4 flex gap-3">
            {sm.facebook && <a href={sm.facebook} target="_blank" rel="noreferrer" className="p-2 rounded-full bg-white/10 hover:bg-dlh-orange transition-colors"><Facebook className="h-4 w-4" /></a>}
            {sm.instagram && <a href={sm.instagram} target="_blank" rel="noreferrer" className="p-2 rounded-full bg-white/10 hover:bg-dlh-orange transition-colors"><Instagram className="h-4 w-4" /></a>}
            {sm.youtube && <a href={sm.youtube} target="_blank" rel="noreferrer" className="p-2 rounded-full bg-white/10 hover:bg-dlh-orange transition-colors"><Youtube className="h-4 w-4" /></a>}
          </div>
        </div>
        <div>
          <h4 className="font-serif font-bold text-dlh-greenLight mb-3">Sekretariat & Bidang</h4>
          <ul className="space-y-2 text-sm">
            {units.map((u) => (
              <li key={u.slug}><Link to={`/bidang/${u.slug}`} className="text-white/75 hover:text-white leading-snug block">{u.short_name}</Link></li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="font-serif font-bold text-dlh-greenLight mb-3">Portal</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/berita" className="text-white/75 hover:text-white">Berita DLH</Link></li>
            <li><Link to="/galeri" className="text-white/75 hover:text-white">Galeri</Link></li>
            <li><Link to="/tentang" className="text-white/75 hover:text-white">Tentang DLH</Link></li>
            <li className="pt-2">
              <Link to="/admin/login" data-testid="footer-login-link" className="inline-flex items-center gap-2 text-dlh-greenLight hover:text-white font-medium">
                <Lock className="h-4 w-4" /> Login Pengelola
              </Link>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4 text-center text-xs text-white/60">
          © {new Date().getFullYear()} Dinas Lingkungan Hidup Kabupaten Mimika. {s.official_portal_status ? "Portal resmi." : "Status portal resmi menunggu konfirmasi pengelola."}
        </div>
      </div>
    </footer>
  );
}

export default function PublicLayout() {
  const [settings, setSettings] = useState(null);
  const [units, setUnits] = useState([]);
  const [topics, setTopics] = useState([]);

  useEffect(() => {
    api.get("/public/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/public/units").then((r) => setUnits(r.data)).catch(() => {});
    api.get("/public/topics").then((r) => setTopics(r.data)).catch(() => {});
  }, []);

  const unitMap = Object.fromEntries(units.map((u) => [u.slug, u]));
  const topicMap = Object.fromEntries(topics.map((t) => [t.slug, t]));

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header settings={settings} units={units} />
      <main className="flex-1">
        <Outlet context={{ settings, units, topics, unitMap, topicMap }} />
      </main>
      <Footer settings={settings} units={units} />
    </div>
  );
}
